import React, { useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Quote } from 'lucide-react';
import PublicHeader from '../../components/public/PublicHeader';
import PublicFooter from '../../components/public/PublicFooter';
import Reveal from '../../components/public/Reveal';
import DemoRequestPanel from '../../components/public/DemoRequestPanel';
import { CONTACT } from '../../config/contact';

const TECH_STACK_GROUPS = [
  {
    title: 'Front-end',
    items: [
      { name: 'React', mark: 'R', logo: 'https://cdn.simpleicons.org/react/61DAFB' },
      { name: 'JavaScript', mark: 'JS', logo: 'https://cdn.simpleicons.org/javascript/F7DF1E' },
      { name: 'Tailwind CSS', mark: 'TW', logo: 'https://cdn.simpleicons.org/tailwindcss/06B6D4' },
      { name: 'React Router', mark: 'RR', logo: 'https://cdn.simpleicons.org/reactrouter/CA4245' },
      { name: 'Chart.js', mark: 'C', logo: 'https://cdn.simpleicons.org/chartdotjs/FF6384' }
    ]
  },
  {
    title: 'API & data',
    items: [
      { name: 'Python', mark: 'PY', logo: 'https://cdn.simpleicons.org/python/3776AB' },
      { name: 'FastAPI', mark: 'FA', logo: 'https://cdn.simpleicons.org/fastapi/009688' },
      { name: 'PostgreSQL', mark: 'PG', logo: 'https://cdn.simpleicons.org/postgresql/4169E1' },
      { name: 'Redis', mark: 'RD', logo: 'https://cdn.simpleicons.org/redis/DC382D' }
    ]
  },
  {
    title: 'Platform & services',
    items: [
      { name: 'Docker', mark: 'DK', logo: 'https://cdn.simpleicons.org/docker/2496ED' },
      { name: 'Nginx', mark: 'NG', logo: 'https://cdn.simpleicons.org/nginx/009639' },
      { name: 'Secure access', mark: 'SEC' },
      { name: 'DGateway', mark: 'DG' }
    ]
  }
];

// Facilities running Ospito+ HMS.
const CLIENT_LOGOS = [
  { name: 'Jubilee Medical & Diagnostic Centre', src: '/images/client-jubilee.png', webp: '/images/client-jubilee.webp', width: 1024, height: 1024 },
  { name: 'Rainbow Community Health Care', src: '/images/client-rainbow.png', webp: '/images/client-rainbow.webp', width: 994, height: 1024 },
  { name: 'Shepherds Healthcare', src: '/images/client-shepherds.png', webp: '/images/client-shepherds.webp', width: 1024, height: 283 }
];

// Temporarily hide public social-proof sections while preserving the implementation.
const SHOW_PUBLIC_SOCIAL_PROOF = false;

const OurStory = () => {
  const [demoPanelOpen, setDemoPanelOpen] = useState(false);

  return (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans selection:bg-teal-500/30 animate-pageFadeIn">
    <PublicHeader />

    {/* Hero */}
    <div className="relative pt-24 sm:pt-28 lg:pt-32 pb-14 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,_var(--tw-gradient-stops))] from-teal-100/60 via-transparent to-transparent dark:from-teal-900/20 pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-teal-200/30 dark:bg-teal-800/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute top-10 -right-24 w-72 h-72 rounded-full bg-cyan-200/30 dark:bg-cyan-800/20 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900 dark:text-white">
          Our{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-500 to-cyan-500">Story</span>
        </h1>
        <div className="mx-auto mt-5 h-1.5 w-24 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 shadow-lg shadow-teal-500/30" />
        <p className="mx-auto mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
          Ospito+ HMS was not designed in a boardroom. It was built by a clinician who lost three
          practices to problems that software should have solved.
        </p>
      </div>
    </div>

    {/* Word from the CEO */}
    <section className="relative overflow-hidden bg-[#0a2540] py-16 sm:py-20">
      {/* Decorative geometry */}
      <div className="pointer-events-none absolute -right-16 top-10 h-64 w-64 rotate-12 rounded-3xl border border-white/5" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 -rotate-12 rounded-3xl border border-white/5" aria-hidden="true" />
      <div className="pointer-events-none absolute right-1/3 -bottom-24 h-64 w-64 rounded-full bg-teal-500/10 blur-3xl" aria-hidden="true" />

      <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.42fr)_minmax(0,0.58fr)] gap-10 lg:gap-14 items-center">
          {/* Portrait */}
          <div className="mx-auto w-full max-w-md lg:max-w-none">
            <div className="relative">
              <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-teal-400/20 to-cyan-400/10 blur-xl" aria-hidden="true" />
              <picture className="relative block">
                <source srcSet="/images/ceo-founder.webp" type="image/webp" />
                <img
                  src="/images/ceo-founder.jpg"
                  alt="Founder and CEO of Ospito Systems"
                  width={1024}
                  height={886}
                  loading="lazy"
                  decoding="async"
                  className="relative block w-full rounded-2xl object-cover shadow-2xl shadow-black/40 ring-1 ring-white/10"
                />
              </picture>
            </div>
          </div>

          {/* Message */}
          <div>
            <div className="flex items-center gap-3">
              <Quote className="w-7 h-7 text-teal-400" aria-hidden="true" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Word From The CEO</h2>
            </div>

            <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-gray-300">
              <p>
                I started my private medical practice in 2021. My first facility failed. I opened a
                second, and it failed too. Then a third — and that one closed as well.
              </p>
              <p>
                It took those three attempts to understand that the problem was never the medicine.
                It was how patient care was delivered: how services were billed and priced, how
                inventory moved through the store, and how staff were managed day to day.
              </p>
              <p>
                I went looking for a system that could fix those things. What I found was either
                fragmented across several tools, far too expensive for a small facility to start
                with, or free and so rigid that it could not solve the problems I actually had.
              </p>
              <p>
                So I teamed up with a group of health workers and software engineers to build what
                was missing — one well-integrated product that is easy to use, effortless to run,
                reliable, and at the same time genuinely affordable.
              </p>
              <p>
                We deployed it at Nasa Healthcare &amp; Surgery, a facility along Namasagali Road in
                Kamuli Municipality. For two years we have run it on real work, under real pressure,
                refining it at every step. Today I am confident this is the future of accountability
                in private medical practice.
              </p>
              <p className="font-semibold text-white">You will love it.</p>
            </div>

            <div className="mt-7 border-t border-white/10 pt-5">
              <p className="text-sm font-bold text-white">Founder &amp; CEO</p>
              <p className="text-sm text-teal-400">Ospito Systems (U) LTD</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Clients */}
    {SHOW_PUBLIC_SOCIAL_PROOF && (
    <section id="clients" className="relative overflow-hidden py-16 sm:py-20">
      <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[32rem] -translate-x-1/2 rounded-full bg-teal-100/40 blur-3xl dark:bg-teal-900/10" aria-hidden="true" />
      <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">Healthcare teams we serve</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">Clients</h2>
          <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500" />
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-gray-600 dark:text-gray-400 sm:text-base">
            Healthcare organizations use Ospito+ HMS to bring patient care, clinical operations, and facility management into one connected workspace.
          </p>
        </Reveal>

        <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-3">
          {CLIENT_LOGOS.map((client) => (
            <Reveal key={client.name}>
              <div className="flex min-h-[11rem] items-center justify-center px-4 py-5 transition-transform duration-300 hover:scale-105">
                <picture className="flex w-full max-w-[18rem] justify-center">
                  <source srcSet={client.webp} type="image/webp" />
                  <img
                    src={client.src}
                    alt={client.name}
                    width={client.width}
                    height={client.height}
                    loading="lazy"
                    decoding="async"
                    className="max-h-36 w-full object-contain"
                  />
                </picture>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    )}

    {/* Technology stack */}
    <section id="technology" className="relative overflow-hidden border-y border-gray-100 bg-white py-16 sm:py-20 dark:border-gray-800 dark:bg-gray-950">
      <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-teal-100/50 blur-3xl dark:bg-teal-900/10" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-cyan-100/50 blur-3xl dark:bg-cyan-900/10" aria-hidden="true" />
      <div className="relative max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">A closer look under the hood</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">A glimpse into the Ospito+ toolbox</h2>
          <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500" />
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-gray-600 dark:text-gray-400 sm:text-base">
            Ospito+ HMS is built on a dependable, modern stack chosen for speed, security, interoperability, and the realities of running healthcare operations every day.
          </p>
        </Reveal>

        <div className="mt-12 space-y-10">
          {TECH_STACK_GROUPS.map((group) => (
            <Reveal key={group.title}>
              <div className="border-t border-gray-200 pt-6 dark:border-gray-800">
                <div className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-gray-900 dark:text-white">{group.title}</h3>
                </div>
                <div className="flex flex-wrap gap-x-8 gap-y-4">
                  {group.items.map((item) => (
                    <div key={item.name} className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                      <span className="relative flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-white text-[10px] font-black tracking-tight text-teal-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-teal-300 dark:ring-gray-700">
                        <span>{item.mark}</span>
                        {item.logo && <img src={item.logo} alt="" width={28} height={28} loading="lazy" decoding="async" onError={(event) => { event.currentTarget.style.display = 'none'; }} className="absolute inset-0 h-full w-full bg-white object-contain p-1" />}
                      </span>
                      <span>{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="pt-16 sm:pt-20 pb-16 sm:pb-20">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-[#0a2540] px-6 py-10 text-center sm:px-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">See what two years of real use built</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base leading-relaxed text-gray-300">
            Request a free 7-day demo to explore the platform yourself, or talk to us about your facility.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setDemoPanelOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-bold shadow-lg shadow-teal-500/25 hover:scale-[1.03] transition-transform"
            >
              Request Demo
              <ArrowRightIcon className="w-4 h-4" />
            </button>
            <a
              href={CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-white/20 transition-colors"
            >
              Talk to us
            </a>
          </div>
        </div>
      </div>
    </section>

    <PublicFooter />
    <DemoRequestPanel isOpen={demoPanelOpen} onClose={() => setDemoPanelOpen(false)} />
  </div>
  );
};

export default OurStory;
